import {computed, Ref, ComputedRef} from 'vue'
import {ITEMS, CONFIGURATION_KEYS, INCOTERM_PICKUP_CUSTOMER_REGEXP} from '~/assets/js/constants'
import {usePlantStore} from '@/store/plants'
import {useConfigurationDataStore} from '@/store/configuration-data'
import {useCartStore} from '@/store/cart'

interface ValidationResult {
  type: string
  message: string
}

interface Variant {
  amount?: string | number
  unit?: string
  subType?: string
  sku?: string
}

interface Address {
  incotermOfWe?: string
}

interface Availability {
  isAvailable?: boolean | null
  stockAvailabilities?: any[]
}

interface Product {
  defaultPlantCode?: string
  otherVariants?: Variant[]
}

interface UseAvailabilitiesOptions {
  variants?: Ref<Variant[]>
  product?: Ref<Product>
  availability?: Ref<Availability | null>
  availabilityDate?: Ref<Date | null>
  isLimitedAvailability?: Ref<boolean>
  availableCertificates?: Ref<any[]>
  customerAddresses?: Ref<Address[]>
  deliveryAddresses?: Ref<Address[]>
}

export function useAvailabilities(options: UseAvailabilitiesOptions = {}) {
  const {
    variants,
    product,
    availability,
    availabilityDate,
    isLimitedAvailability,
    availableCertificates,
    customerAddresses,
    deliveryAddresses
  } = options

  const getVariants = (): Variant[] => {
    return variants?.value ? variants.value : []
  }

  const getVariantText = (variant: Variant | null | undefined, $tv: any): string => {
    if (!variant) {
      return ''
    }
    const packageUnitText = (`${variant.amount || ''} ${$tv(variant.unit, 'packageUnits')}`).trim()
    let variantText = ''
    if (variant.subType === 'PI' || variant.subType === 'PS') {
      variantText = `${$tv(variant.subType, 'variants')} ${packageUnitText ? '(' + packageUnitText + ')' : ''} ${variant?.sku ?? ''}`
    } else {
      variantText = `${$tv(variant.subType, 'variants')} ${variant?.sku ?? ''}`
    }
    return variantText.trim()
  }

  const isPickupCustomerAddress = (customer: Address | undefined, addressCustomer: Address | undefined): boolean => {
    const regex = INCOTERM_PICKUP_CUSTOMER_REGEXP
    return !!(customer?.incotermOfWe &&
      regex.test(customer.incotermOfWe) &&
      addressCustomer?.incotermOfWe &&
      regex.test(addressCustomer.incotermOfWe))
  }

  const validateAvailabilityDate: ComputedRef<ValidationResult> = computed(() => {
    let result: ValidationResult = {
      type: ITEMS.VALIDATION_TYPES.OK,
      message: ITEMS.AVAILABILITY_DATE_MESSAGES.NONE
    }

    if (isLimitedAvailability?.value) {
      result.type = ITEMS.VALIDATION_TYPES.NONE
      return result
    }

    // error tooltips
    if (availability?.value === null) {
      result.type = ITEMS.VALIDATION_TYPES.ERROR
      result.message = ITEMS.AVAILABILITY_DATE_MESSAGES.NOT_LOADED
      return result
    }
    const today = new Date().setHours(0, 0, 0, 0)
    const maxDate = new Date().setFullYear(new Date().getFullYear() + 2)
    const dateCopy = availabilityDate?.value ? new Date(availabilityDate.value.getTime()) : availabilityDate?.value
    const availabilityDateTime = dateCopy ? dateCopy.getTime() : null
    if (availabilityDateTime && availabilityDateTime <= today) {
      result.type = ITEMS.VALIDATION_TYPES.ERROR
      result.message = ITEMS.AVAILABILITY_DATE_MESSAGES.DATEPASTINVALID
      return result
    }

    if (availabilityDateTime && availabilityDateTime > maxDate) {
      result.type = ITEMS.VALIDATION_TYPES.ERROR
      result.message = ITEMS.AVAILABILITY_DATE_MESSAGES.DATEFUTUREINVALID
      return result
    }

    // warning tooltips
    const isAvailable = availability?.value?.isAvailable ?? null
    if (isAvailable === false) {
      result.type = ITEMS.VALIDATION_TYPES.WARNING
    }

    return result
  })

  const stockScheduleRows: ComputedRef<any[]> = computed(() => {
    return availability?.value?.stockAvailabilities ?? []
  })

  const hasMultipleWarehouses: ComputedRef<boolean> = computed(() => {
    if (!product?.value) {
      return false
    }
    const plants = usePlantStore().allPlants?.filter((fw: any) => fw.plant === product.value?.defaultPlantCode)
    const showAlternativePlants: any = useConfigurationDataStore().configurations?.find((config: any) => config.key === CONFIGURATION_KEYS.SHOW_ALTERNATIVE_PLANTS_KEY)
    return !!(plants && plants.length > 1 && showAlternativePlants?.value === 'True')
  })

  const hasCertificateChange: ComputedRef<boolean> = computed(() => {
    const showCertificateChange: any = useConfigurationDataStore().configurations?.find((config: any) => config.key === CONFIGURATION_KEYS.SHOW_CERTIFICATES_KEY)
    return !!(availableCertificates?.value && availableCertificates.value.length >= 1 && showCertificateChange?.value === 'True')
  })

  const hasVariantChange: ComputedRef<boolean> = computed(() => {
    const variantList = product?.value ? product.value.otherVariants : getVariants()
    const showVariantChange: any = useConfigurationDataStore().configurations?.find((config: any) => config.key === CONFIGURATION_KEYS.SHOW_VARIANTS_KEY)
    return !!(variantList && variantList.length >= 1 && showVariantChange?.value === 'True')
  })

  const showShippingFee: ComputedRef<boolean> = computed(() => {
    const showShippingFeeConfig: any = useConfigurationDataStore().configurations?.find((config: any) => config.key === CONFIGURATION_KEYS.SHOW_SHIPPING_FEE_KEY)
    let customer = customerAddresses?.value && customerAddresses.value.length > 0 ? customerAddresses.value[0] : useCartStore().selectedAddresses.invoice
    let delivery = deliveryAddresses?.value && deliveryAddresses.value.length > 0 ? deliveryAddresses.value[0] : useCartStore().selectedAddresses.delivery
    return !(isPickupCustomerAddress(customer ?? undefined, delivery ?? undefined) && showShippingFeeConfig?.value === 'False')
  })

  return {
    getVariants,
    getVariantText,
    isPickupCustomerAddress,
    validateAvailabilityDate,
    stockScheduleRows,
    hasMultipleWarehouses,
    hasCertificateChange,
    hasVariantChange,
    showShippingFee
  }
}
